"use client";

import React, { useMemo } from "react";
import {
  addHours,
  areIntervalsOverlapping,
  differenceInMinutes,
  getHours,
  getMinutes,
  isSameDay,
  startOfDay,
} from "date-fns";

import { cn } from "../../lib/utils";
import { DraggableEvent } from "./draggable-event";
import { DroppableCell } from "./droppable-cell";
import { EventItem } from "./event-item";
import { isMultiDayEvent } from "./utils";
import { useCurrentTimeIndicator } from "./hooks/use-current-time-indicator";
import {
  WeekCellsHeight,
  EndHour,
  StartHour,
  TimeSlotIntervalMinutes,
} from "./constants";
import { formatTimeString, resolveTimeZone } from "./time-utils";

const computePositionedEvents = (
  events,
  currentDate,
  businessStartHour
) => {
  const result = [];
  const dayStart = startOfDay(currentDate);

  const sortedEvents = [...events].sort((a, b) => {
    const aStart = new Date(a.start);
    const bStart = new Date(b.start);
    const aEnd = new Date(a.end);
    const bEnd = new Date(b.end);

    if (aStart < bStart) return -1;
    if (aStart > bStart) return 1;

    const aDuration = differenceInMinutes(aEnd, aStart);
    const bDuration = differenceInMinutes(bEnd, bStart);
    return bDuration - aDuration;
  });

  const columns = [];

  sortedEvents.forEach((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    const adjustedStart = isSameDay(currentDate, eventStart)
      ? eventStart
      : dayStart;
    const adjustedEnd = isSameDay(currentDate, eventEnd)
      ? eventEnd
      : addHours(dayStart, 24);

    const startHour = getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
    const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;
    const top = (startHour - businessStartHour) * WeekCellsHeight;
    const height = (endHour - startHour) * WeekCellsHeight;

    let columnIndex = 0;
    let placed = false;

    while (!placed) {
      const col = columns[columnIndex] || [];
      if (col.length === 0) {
        columns[columnIndex] = col;
        placed = true;
      } else {
        const overlaps = col.some((c) =>
          areIntervalsOverlapping(
            { start: adjustedStart, end: adjustedEnd },
            { start: new Date(c.event.start), end: new Date(c.event.end) }
          )
        );
        if (!overlaps) {
          placed = true;
        } else {
          columnIndex++;
        }
      }
    }

    const currentColumn = columns[columnIndex] || [];
    columns[columnIndex] = currentColumn;
    currentColumn.push({ event, end: adjustedEnd });

    const width = columnIndex === 0 ? 1 : 1 - columnIndex * 0.1;
    const left = columnIndex === 0 ? 0 : columnIndex * 0.1;

    result.push({
      event,
      top,
      height,
      left,
      width,
      zIndex: 10 + columnIndex,
    });
  });

  return result;
};

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 */

export function DayView({
  currentDate,
  events,
  onEventSelect,
  onEventCreate,
  timezone,
  timeFormat = "24h",
  businessHours,
  resources = [],
  resourceMode = "doctors",
}) {
  const dayKey = DAY_KEYS[currentDate.getDay()];
  const daySchedule = businessHours?.byDay?.[dayKey];
  const dayEnabled = daySchedule?.enabled !== false;
  const businessStartHour =
    daySchedule?.startHour ?? businessHours?.startHour ?? StartHour;
  const businessEndHour = Math.max(
    daySchedule?.endHour ?? businessHours?.endHour ?? EndHour,
    businessStartHour + 1
  );
  const resolvedTimeZone = resolveTimeZone(timezone);
  const segmentsPerHour = Math.max(
    1,
    Math.round(60 / TimeSlotIntervalMinutes)
  );
  const hours = useMemo(() => {
    const dayStart = startOfDay(currentDate);
    const slots = [];
    const startHourInt = Math.floor(businessStartHour);
    const endHourInt = Math.ceil(businessEndHour);

    for (let hour = startHourInt; hour < endHourInt; hour++) {
      slots.push({
        date: addHours(dayStart, hour),
        hourValue: hour,
      });
    }

    return slots;
  }, [currentDate, businessStartHour, businessEndHour]);

  const dayEvents = useMemo(() => {
    return events
      .filter((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        return (
          isSameDay(currentDate, eventStart) ||
          isSameDay(currentDate, eventEnd) ||
          (currentDate > eventStart && currentDate < eventEnd)
        );
      })
      .sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
  }, [currentDate, events]);

  const allDayEvents = useMemo(() => {
    return dayEvents.filter((event) => event.allDay || isMultiDayEvent(event));
  }, [dayEvents]);

  const timeEvents = useMemo(() => {
    return dayEvents.filter(
      (event) => !event.allDay && !isMultiDayEvent(event)
    );
  }, [dayEvents]);

  const hasCustomResources = resources.length > 0;

  const resourcesToRender = useMemo(() => {
    if (resources.length === 0) {
      return [
        {
          id: "default-schedule",
          name: "Schedule",
        },
      ];
    }
    return resources;
  }, [resources]);

  const resourceKey = resourceMode === "chairs" ? "chairId" : "doctorId";
  const resourceIds = useMemo(() => new Set(resourcesToRender.map((r) => r.id)), [resourcesToRender]);

  const resourceColumns = useMemo(() => {
    return resourcesToRender.map((resource, index) => {
      const eventsForResource = timeEvents.filter((event) => {
        const eventResourceId = event?.[resourceKey];
        if (!eventResourceId) {
          return index === 0;
        }
        if (!resourceIds.has(eventResourceId)) {
          return index === 0;
        }
        return eventResourceId === resource.id;
      });

      return {
        resource,
        positionedEvents: computePositionedEvents(
          eventsForResource,
          currentDate,
          businessStartHour
        ),
      };
    });
    }, [
    timeEvents,
    resourcesToRender,
    resourceKey,
    currentDate,
    businessStartHour,
    resourceIds,
  ]);

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    onEventSelect(event);
  };

  const showAllDaySection = allDayEvents.length > 0;
  const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(
    currentDate,
    "day",
    {
      startHour: businessStartHour,
      endHour: businessEndHour,
    }
  );

  const gridTemplateColumns = `minmax(3rem, 4rem) repeat(${resourceColumns.length}, minmax(0, 1fr))`;

  const handleCellClick = (hourValue, minuteOffset, resourceId) => {
    const slotTime = hourValue + minuteOffset / 60;
    if (
      !dayEnabled ||
      slotTime < businessStartHour ||
      slotTime >= businessEndHour
    ) {
      return;
    }

    const startTime = new Date(currentDate);
    startTime.setHours(hourValue);
    startTime.setMinutes(minuteOffset);

    const meta = hasCustomResources
      ? resourceMode === "chairs"
        ? { chairId: resourceId }
        : { doctorId: resourceId }
      : {};

    onEventCreate(startTime, meta);
  };

  return (
    <div data-slot="day-view" className="contents">
      <div
        className="border-border/70 grid border-x border-t bg-muted/40 text-xs"
        style={{ gridTemplateColumns }}
      >
        <div className="flex items-center justify-end px-2 py-3 text-[10px] uppercase tracking-wide text-muted-foreground sm:px-4">
          Time
        </div>
        {resourceColumns.map(({ resource }) => (
          <div
            key={resource.id}
            className="border-l border-border/60 px-3 py-3"
          >
            <div className="text-sm font-semibold leading-tight">
              {resource.name}
            </div>
            {resource.details && (
              <div className="text-xs text-muted-foreground">
                {resource.details}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAllDaySection && (
        <div className="border-border/70 border-x border-b bg-muted/30">
          <div className="grid" style={{ gridTemplateColumns }}>
            <div className="relative px-2 py-2 text-[10px] uppercase tracking-wide text-muted-foreground sm:px-4 sm:text-xs">
              All day
            </div>
            <div
              className="border-l border-border/70 p-1"
              style={{ gridColumn: `2 / span ${resourceColumns.length}` }}
            >
              {allDayEvents.map((event) => {
                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end);
                const isFirstDay = isSameDay(currentDate, eventStart);
                const isLastDay = isSameDay(currentDate, eventEnd);

                return (
                  <EventItem
                    key={`spanning-${event.id}`}
                    onClick={(e) => handleEventClick(event, e)}
                    event={event}
                    view="month"
                    isFirstDay={isFirstDay}
                    isLastDay={isLastDay}
                    timezone={resolvedTimeZone}
                    timeFormat={timeFormat}
                  >
                    <div>{event.title}</div>
                  </EventItem>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="border-border/70 border-x border-b">
        <div
          className="grid overflow-hidden"
          style={{ gridTemplateColumns }}
        >
          <div>
            {hours.map(({ date: hourDate }, index) => (
              <div
                key={hourDate.toISOString()}
                className="border-border/70 relative h-[var(--week-cells-height)] border-b last:border-b-0"
              >
                {index > 0 && (
                  <span className="bg-background text-muted-foreground/70 absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                    {formatTimeString(hourDate, resolvedTimeZone, timeFormat, {
                      omitMinutesWhenZero: true,
                    })}
                  </span>
                )}
              </div>
            ))}
          </div>

          {resourceColumns.map(({ resource, positionedEvents }, resourceIndex) => (
            <div
              key={resource.id}
              className={cn(
                "relative border-l border-border/70",
                resourceIndex === 0 && "border-l-0",
                !dayEnabled && "bg-muted/40"
              )}
            >
              {!dayEnabled && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center text-xs font-medium text-muted-foreground">
                  Clinic closed
                </div>
              )}
              {positionedEvents.map((positionedEvent) => (
                <div
                  key={positionedEvent.event.id}
                  className="absolute z-10 px-0.5"
                  style={{
                    top: `${positionedEvent.top}px`,
                    height: `${positionedEvent.height}px`,
                    left: `${positionedEvent.left * 100}%`,
                    width: `${positionedEvent.width * 100}%`,
                    zIndex: positionedEvent.zIndex,
                  }}
                >
                  <div className="h-full w-full">
                    <DraggableEvent
                      event={positionedEvent.event}
                      view="day"
                      onClick={(e) =>
                        handleEventClick(positionedEvent.event, e)
                      }
                      showTime
                      height={positionedEvent.height}
                      timezone={resolvedTimeZone}
                      timeFormat={timeFormat}
                    />
                  </div>
                </div>
              ))}

              {dayEnabled && currentTimeVisible && (
                <div
                  className="pointer-events-none absolute right-0 left-0 z-20"
                  style={{ top: `${currentTimePosition}%` }}
                >
                  <div className="relative flex items-center">
                    <div className="bg-primary absolute -left-1 h-2 w-2 rounded-full"></div>
                    <div className="bg-primary h-[2px] w-full"></div>
                  </div>
                </div>
              )}

              {hours.map(({ date: hourDate, hourValue }) => (
                <div
                  key={`${hourDate.toISOString()}-${resource.id}`}
                  className="border-border/70 relative h-[var(--week-cells-height)] border-b last:border-b-0"
                >
                  {Array.from({ length: segmentsPerHour }).map((_, segment) => {
                    const minuteOffset = segment * TimeSlotIntervalMinutes;
                    const segmentTime = hourValue + minuteOffset / 60;
                    const slotWithinSchedule =
                      dayEnabled &&
                      segmentTime >= businessStartHour &&
                      segmentTime < businessEndHour;
                    return (
                      <DroppableCell
                        key={`${hourDate.toISOString()}-${resource.id}-${segment}`}
                        id={`day-cell-${currentDate.toISOString()}-${resource.id}-${segmentTime}`}
                        date={currentDate}
                        time={segmentTime}
                        resourceId={resource.id}
                        resourceType={resourceMode}
                        className={cn(
                          "absolute w-full",
                          segment === 0 && "top-0",
                          !slotWithinSchedule &&
                            "pointer-events-none opacity-40"
                        )}
                        style={{
                          height: `${100 / segmentsPerHour}%`,
                          top: `${(segment / segmentsPerHour) * 100}%`,
                        }}
                        disabled={!slotWithinSchedule}
                        onClick={() =>
                          handleCellClick(hourValue, minuteOffset, resource.id)
                        }
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
